import { useCurrentBoxId } from "@/modules/core/store/useCurrentSpaceManagerStore"
import { BoxDetailsEntity } from "@modules/box/entities/boxEntity"
import { useServicesContext } from "@modules/core/services/services.context"
import * as Sentry from "@sentry/react"
import { useQuery } from "@tanstack/react-query"
import { useObservable } from "micro-observables"
import { useEffect } from "react"
import { useLocation } from "react-router"

/**
 * Syncs app context to Sentry for triage: current route, anonymized user role, and feature flags.
 * Must be mounted inside BrowserRouter and ServicesContextProvider.
 */
export function SentryContextSync() {
  const location = useLocation()
  const { featureToggleService, boxApi } = useServicesContext()
  const boxId = useCurrentBoxId()
  // For Sentry we just need currentUserRole when it’s available; it’s fine if it’s missing during initial load.
  const { data: box } = useQuery({
    queryKey: ["box", "sentry-context", boxId],
    queryFn: () => (boxId ? boxApi.getBox(boxId).then((dto) => BoxDetailsEntity.fromDto(dto)) : null),
    enabled: !!boxId,
    throwOnError: false,
  })
  const featureToggles = useObservable(featureToggleService.featureToggles)

  const route = location.pathname
  const role = getAnonymizedRole(route, boxId, box?.currentUserRole)
  const featureFlags = featureToggles.filter((f) => f.isEnabled).map((f) => f.key)

  useEffect(() => {
    Sentry.setContext("page", { route })
  }, [route])

  useEffect(() => {
    Sentry.setContext("triage", {
      role,
      featureFlags: featureFlags.length > 0 ? featureFlags : undefined,
    })
  }, [role, featureFlags])

  return null
}

function getAnonymizedRole(pathname: string, selectedBoxId: string | null, boxRole: string | undefined | null): string {
  if (pathname.startsWith("/my-space")) {
    return "athlete"
  }
  if (pathname.startsWith("/box/") && selectedBoxId) {
    return boxRole ?? "box_member"
  }
  if (/^\/(login|sign-up|register|welcome|confirm-email|reset-password|send-code)/.test(pathname)) {
    return "auth_guest"
  }
  return "anonymous"
}
